import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import PageTitle from "../../../layouts/PageTitle";
import {
  useGetWorkplaceSettingsQuery,
  useGetAllWorkplaceSettingsQuery,
  useGetAllProjectsForMatrixQuery,
  appApi,
} from "../../../../store/appApi";
import { setWorkplaceSettingsApi } from "../../../../services/attendance";
import GeofenceMapPicker from "./GeofenceMapPicker";

const WorkplaceSettings = () => {
  const dispatch = useDispatch();
  const { data: projectsData } = useGetAllProjectsForMatrixQuery();
  const projects = projectsData?.data ?? projectsData?.projects ?? [];

  const [selectedProjectId, setSelectedProjectId] = useState("");

  const { data, isLoading, isFetching } = useGetWorkplaceSettingsQuery(
    selectedProjectId || undefined,
  );
  const current = data?.settings;

  const { data: allData, isLoading: allLoading } =
    useGetAllWorkplaceSettingsQuery();
  const allSettings = allData?.settings || [];

  const [form, setForm] = useState({
    siteName: "",
    targetLat: "",
    targetLng: "",
    toleranceMeters: 50,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (current) {
      setForm({
        siteName: current.siteName || "",
        targetLat: current.targetLat ?? "",
        targetLng: current.targetLng ?? "",
        toleranceMeters: current.toleranceMeters ?? 50,
      });
    } else {
      setForm({ siteName: "", targetLat: "", targetLng: "", toleranceMeters: 50 });
    }
  }, [current, selectedProjectId]);

  const handle = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await setWorkplaceSettingsApi(
        {
          siteName: form.siteName,
          targetLat: parseFloat(form.targetLat),
          targetLng: parseFloat(form.targetLng),
          toleranceMeters: parseInt(form.toleranceMeters, 10),
        },
        selectedProjectId || undefined,
      );
      dispatch(appApi.util.invalidateTags(["WorkplaceSettings"]));
      Swal.fire("Saved", "Workplace settings updated.", "success");
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Save failed.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const useCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          targetLat: pos.coords.latitude.toFixed(7),
          targetLng: pos.coords.longitude.toFixed(7),
        }));
      },
      () => Swal.fire("Error", "GPS not available.", "error"),
      { enableHighAccuracy: true },
    );
  };

  return (
    <>
      <PageTitle activeMenu="Workplace Config" motherMenu="Attendance" />
      <div className="row">
        <div className="col-xl-6 col-lg-8 mx-auto">
          <div className="card">
            <div className="card-header">
              <h4 className="card-title">Set Workplace Location</h4>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Site</label>
                <select
                  className="form-control"
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                >
                  <option value="">Default / Global (no project)</option>
                  {projects.map((project) => (
                    <option
                      key={project._id || project.id}
                      value={project._id || project.id}
                    >
                      {project.projectName}
                      {project.city ? ` — ${project.city}` : ""}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  Pick a project to set its geofence directly — no need to
                  switch your active project first.
                </div>
              </div>

              {isLoading || isFetching ? (
                <div className="text-center py-3">Loading…</div>
              ) : (
                <form onSubmit={save}>
                  <GeofenceMapPicker
                    lat={parseFloat(form.targetLat)}
                    lng={parseFloat(form.targetLng)}
                    radiusMeters={form.toleranceMeters}
                    onChange={({ lat, lng }) =>
                      setForm((f) => ({
                        ...f,
                        targetLat: lat.toFixed(7),
                        targetLng: lng.toFixed(7),
                      }))
                    }
                  />
                  <div className="mb-3">
                    <label className="form-label">Site Name</label>
                    <input
                      name="siteName"
                      className="form-control"
                      value={form.siteName}
                      onChange={handle}
                      placeholder="e.g. Main Office"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Latitude</label>
                    <input
                      name="targetLat"
                      type="number"
                      step="any"
                      className="form-control"
                      value={form.targetLat}
                      onChange={handle}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Longitude</label>
                    <input
                      name="targetLng"
                      type="number"
                      step="any"
                      className="form-control"
                      value={form.targetLng}
                      onChange={handle}
                      required
                    />
                  </div>
                  {current?.address && (
                    <div className="mb-3 text-muted small">
                      <strong>Address:</strong> {current.address}
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">
                      Tolerance Radius (meters)
                    </label>
                    <input
                      name="toleranceMeters"
                      type="number"
                      min="10"
                      max="5000"
                      className="form-control"
                      value={form.toleranceMeters}
                      onChange={handle}
                      required
                    />
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? "Saving…" : "Save Settings"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={useCurrentLocation}
                    >
                      Use My Location
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Overview of every configured site across all projects */}
          <div className="card mt-4">
            <div className="card-header">
              <h4 className="card-title">All Configured Sites</h4>
            </div>
            <div className="card-body">
              {allLoading ? (
                <div className="text-center py-3">Loading…</div>
              ) : allSettings.length === 0 ? (
                <div className="text-center text-muted py-3">
                  No sites configured yet.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Site Name</th>
                        <th>Address</th>
                        <th>Latitude</th>
                        <th>Longitude</th>
                        <th>Radius (m)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allSettings.map((setting) => (
                        <tr key={setting._id}>
                          <td>
                            {setting.projectId?.projectName ||
                              "Default / Global"}
                          </td>
                          <td>{setting.siteName}</td>
                          <td className="text-muted small">{setting.address || "—"}</td>
                          <td>{setting.targetLat}</td>
                          <td>{setting.targetLng}</td>
                          <td>{setting.toleranceMeters}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkplaceSettings;
